import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, OverlayTrigger, Tooltip, Tab, Tabs } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';

//elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import SwitchWidget from '../../Admin/ControlPanel/SwitchWidget.js'

//assets
import CloseIcon from "~/assets/img/modal-close-icon.svg";
import { permissions, modulePermissionsMap } from '../../../utils/constants.js';
import { checkCircleSVG, crossCircleSVG, saveSVG, editSVG, backSVG } from '../../../utils/constants';
import RosterPermissionsTab from './RosterPermissionsTab';


class UserPermissions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            modulePermissions: [],
            currentId: null,
            currentPermission: null,
            userDetails: null,
            activeTab: 'general',
            originalModulePermissions: null,
        }
    }

    componentDidMount() {
        const { userId } = this.props;
        if (!userId) return;
        this.setState({ loading: true })
        this.loadData();
    }

    async loadData() {
        const { userStore, userId, commonStore, t } = this.props
        try {
            const { userPermissions, userDetails } = await userStore.getUserPermissions(userId);
            const _modulePermissions = [];
            userPermissions && userPermissions.length && userPermissions.forEach(ele => {
                _modulePermissions.push({
                    module: ele.module,
                    permissions: ele.permissions
                })
            });
            this.setState({ modulePermissions: _modulePermissions, userDetails, loading: false, currentId: null, currentPermission: null })
        }
        catch (err) {
            commonStore.addNotification(t('Something went wrong'), null, 'error');
            this.setState({ loading: false, currentId: null, currentPermission: null });
        }

    }

    handlePermissionChange = (permissionName, value) => {
        const { currentPermission, modulePermissions, originalModulePermissions } = this.state;
        if (!currentPermission) return;

        const permissions = { ...currentPermission.permissions };
        permissions[permissionName] = value;

        const moduleConfig = modulePermissionsMap[currentPermission.module_name];
        if (moduleConfig) {
            if (value) {
                const permConfig = moduleConfig.permissions.find(p => p.value === permissionName);
                if (permConfig && permConfig.requires) {
                    permConfig.requires.forEach(key => { permissions[key] = true; });
                }
            } else {
                moduleConfig.permissions.forEach(p => {
                    if (p.requires && p.requires.includes(permissionName)) {
                        permissions[p.value] = false;
                    }
                });
            }
        }

        // Auto-enable Manual Roster View when Quick Planner or Work Pattern access is enabled.
        const moduleName = currentPermission.module_name;
        const ROSTER_SUB_MODULES = new Set(['md_QuickPlanner', 'md_WorkPattern']);
        let updatedModulePermissions = modulePermissions;

        if (moduleName === 'md_Roster' && permissionName === 'view') {
            if (!value) {
                updatedModulePermissions = modulePermissions.map(m => {
                    if (!ROSTER_SUB_MODULES.has(m.module)) return m;
                    return { ...m, permissions: Object.fromEntries(Object.keys(m.permissions).map(k => [k, false])) };
                });
            } else if (originalModulePermissions) {
                updatedModulePermissions = modulePermissions.map(m => {
                    if (!ROSTER_SUB_MODULES.has(m.module)) return m;
                    return originalModulePermissions.find(o => o.module === m.module) || m;
                });
            }
        } else if (ROSTER_SUB_MODULES.has(moduleName) && value) {
            const rosterModule = modulePermissions.find(m => m.module === 'md_Roster');
            if (rosterModule && !rosterModule.permissions.view) {
                updatedModulePermissions = modulePermissions.map(m =>
                    m.module === 'md_Roster' ? { ...m, permissions: { ...m.permissions, view: true } } : m
                );
            }
        }

        this.setState({ currentPermission: { ...currentPermission, permissions }, modulePermissions: updatedModulePermissions });
    }

    handleEdit = (module, permissions) => {
        const { userId } = this.props;
        const { modulePermissions } = this.state;
        const _currrentPermission = {
            user_id: userId,
            module_name: module,
            permissions: { ...permissions }
        }
        this.setState({ currentId: module, currentPermission: _currrentPermission, originalModulePermissions: modulePermissions })
    }

    handleSave = () => {
        const { currentPermission } = this.state;
        if (currentPermission && currentPermission.module_name === 'md_Deviation') {
            currentPermission.permissions.create = true;
            currentPermission.permissions.manage = true;
            currentPermission.permissions.manageMembers = true;
        }
        this.setState({ loading: true }, this.updateUserPermissions);
    }

    updateUserPermissions = async () => {
        const { userStore, commonStore, t } = this.props;
        const { currentPermission, modulePermissions, originalModulePermissions } = this.state;
        try {
            await userStore.setUserPermissions(currentPermission);
            if (originalModulePermissions) {
                const cascaded = modulePermissions.filter(m => {
                    if (m.module === currentPermission.module_name) return false;
                    const orig = originalModulePermissions.find(o => o.module === m.module);
                    return orig && JSON.stringify(orig.permissions) !== JSON.stringify(m.permissions);
                });
                for (const m of cascaded) {
                    await userStore.setUserPermissions({ user_id: currentPermission.user_id, module_name: m.module, permissions: m.permissions });
                }
            }
            this.loadData();
        }
        catch (err) {
            commonStore.addNotification(t('Something went wrong'), null, 'error');
            this.setState({ loading: false });
        }
    }

    resetCurrentPermission = () => {
        const { originalModulePermissions } = this.state;
        this.setState({
            currentPermission: null,
            currentId: null,
            ...(originalModulePermissions && { modulePermissions: originalModulePermissions, originalModulePermissions: null })
        });
    }

    componentWillUnmount = () => {
        this.resetCurrentPermission();
    }

    renderRosterTab = () => {
        const { t } = this.props;
        const { modulePermissions, currentId, currentPermission } = this.state;
        return (
            <RosterPermissionsTab
                t={t}
                modulePermissions={modulePermissions}
                currentId={currentId}
                currentPermission={currentPermission}
                onEdit={this.handleEdit}
                onSave={this.handleSave}
                onCancel={this.resetCurrentPermission}
                onPermissionChange={this.handlePermissionChange}
            />
        );
    }

    renderModalBody = () => {
        const { userId, t, commonStore } = this.props;
        const { loading, modulePermissions, currentId, currentPermission, activeTab, userDetails } = this.state;
        const { data: newClientData } = commonStore.config?.client || {};
        const { workPlanSettings } = newClientData?.basicRules || {};
        if (!userId) return <div>User Id not found</div>
        if (loading) return <div>{t('Hold On, This Will take a moment!')}</div>
        if (userDetails && (userDetails.userType === 'member' || this.props.userStore.currentUser.user_type === 'pm')) {
            return this.renderRosterTab();
        }
        const generalModules = modulePermissions.filter(ele => !modulePermissionsMap[ele.module]);
        return (
            <Tabs
                activeKey={activeTab}
                onSelect={(key) => this.setState({ activeTab: key, currentId: null, currentPermission: null })}
                id="UserPermissionsTabs"
                className="px-4"
            >
                <Tab eventKey="general" title={t('General')}>
                    <div className="table-responsive">
                        <table className='table'>
                            <thead>
                                <tr>
                                    <th>{t('Module')}</th>
                                    {permissions.map((ele) => {
                                        return <th key={ele.value}>{t(ele.label)}
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id="tooltip-top">
                                                        {t(ele.description)}.
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="fa fa-info-circle text-muted opacity-50" aria-hidden="true"></i>
                                            </OverlayTrigger></th>
                                    })}
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {generalModules && generalModules.length ? generalModules.map((ele, index) => {
                                    const isDeviation = ele.module === 'md_Deviation';
                                    const isEditing = currentId && currentId === ele.module;
                                    return (
                                        <tr key={`md_0_${index}`} className='text-center'>
                                            <td>{t(ele.module)}</td>
                                            {permissions.map((e, i) => {
                                                const lockedTrue = isDeviation && e.value !== 'remove';
                                                return (
                                                    <th key={`permi_0_${i}`} className="border-bottom-0">
                                                        {isEditing ?
                                                            <SwitchWidget
                                                                value={lockedTrue ? true : currentPermission.permissions[e.value]}
                                                                onChange={lockedTrue ? () => { } : checked => this.handlePermissionChange(e.value, checked)}
                                                                disabled={lockedTrue}
                                                            />
                                                            :
                                                            (lockedTrue || ele.permissions[e.value])
                                                                ? checkCircleSVG({ width: 16, fillColor: '#16C51D' })
                                                                : crossCircleSVG({ width: 18, fillColor: '#FF0000' })}
                                                    </th>
                                                );
                                            })}
                                            <th className="border-bottom-0">
                                                {currentId && currentId === ele.module ?
                                                    <>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id="tooltip-top">{this.props.t('Update Permissions')}</Tooltip>}
                                                        >
                                                            <Button icon_sm fill
                                                                onClick={this.handleSave}>
                                                                {saveSVG({ width: 20, fillColor: '#2550AC' })}
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id="tooltip-top">{this.props.t('Cancel')}</Tooltip>}
                                                        >
                                                            <Button icon_sm fill ms2
                                                                onClick={this.resetCurrentPermission}>
                                                                {backSVG({ width: 20, fillColor: '#2550AC' })}
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </>
                                                    :
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id="tooltip-top">{this.props.t('Change Permissions')}</Tooltip>}
                                                    >
                                                        <Button icon_sm fill
                                                            disabled={!!currentId}
                                                            onClick={() => this.handleEdit(ele.module, ele.permissions)}>
                                                            {editSVG({ width: 20, fillColor: '#2550AC' })}
                                                        </Button>
                                                    </OverlayTrigger>
                                                }
                                            </th>
                                        </tr>
                                    );
                                })
                                    :
                                    <tr className='text-center'>
                                        <td colSpan={6}>
                                            <span className='font-size-15 fw-bold text-muted'>{t('No data found')}</span>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </Tab>
                {workPlanSettings?.allow_work_hours_settings && <Tab eventKey="roster" title={t('Roster')}>
                    {this.renderRosterTab()}
                </Tab>}
            </Tabs>
        );
    }

    render() {
        const { t, userId, handleModalState } = this.props;
        const { userDetails } = this.state;
        return (
            <Modal
                size={userDetails && (userDetails.userType === 'member' || this.props.userStore.currentUser.user_type === 'pm') ? 'lg' : 'xl'}
                className="modal fade"
                show={!!userId}
                onHide={() => handleModalState('showUserPermissionModal', false, false)}
            >
                <Modal.Header>
                    <Modal.Title>{t('Manage User Permissions')}</Modal.Title>
                    <div className='modal-header__revised__closebtn' onClick={() => handleModalState('showUserPermissionModal', false, false)}>
                        <img
                            src={CloseIcon}
                            className="cursor-pointer"
                            alt='close_button'
                        />
                    </div>
                </Modal.Header>
                <Modal.Body className='mb-2'>
                    <div className='d-flex justify-content-between align-items-center bg-light rounded mx-3 px-3 py-2 mb-3'>
                        <span className='font-size-12'>{t('User')}:&nbsp;<span className='font-size-15 fw-bold'>{userDetails?.fullname}</span></span>
                        <span className='font-size-12'>{t('User type')}:&nbsp;<span className='font-size-15 fw-bold'>{(userDetails?.userType || '').toUpperCase()}</span></span>
                    </div>
                    {this.renderModalBody()}
                </Modal.Body>
            </Modal>
        );
    }
}
export default inject('userStore', 'commonStore')(applicationRouter(withLocalization(observer(UserPermissions))));
